import React, { useState, useEffect, useReducer } from "react";
import { get } from "aws-amplify/api";
import { post } from "aws-amplify/api";
import currentUserToken from "CurrentUserToken";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  IconButton,
} from "@mui/material";
import {
  Edit as EditIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
} from "@mui/icons-material"; // Import delle icone
import dayjs from "dayjs";
import { APIStatusDialog } from "components/APIStatusDialog";
import { Tooltip } from "@mui/material";
import { I18n } from "aws-amplify/utils";
const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};

const SuggestionDialog = ({ open, onClose, onSave, alarmTypeId }) => {
  const [state, dispatch] = useReducer(reducer, {
    audit: [],
    fetched: false,
  });
  const [suggestions, setSuggestions] = useState({});
  const [isEditable, setIsEditable] = useState(false);
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });

  // Fetch alarm descriptions
  const tlmGetSuggestion = async () => {
    try {
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/suggestion/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            alarm_type_id: alarmTypeId,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();

      setSuggestions(response);
      dispatch({ type: "DATA_RECEIVED", value: response });
    } catch (error) {
      console.error("Error adding language:", error);
    }
  };

  useEffect(() => {
    tlmGetSuggestion();
  }, [alarmTypeId]);

  // Update suggestion
  const tlmNewSuggestion = async () => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Adding suggestion"),
      });
      const restOperation = post({
        apiName: "TelemonitoringAPI",
        path: "/suggestion/new",
        options: {
          body: suggestions,
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            alarm_type_id: alarmTypeId,
          },
        },
      });
      const { body, statusCode } = await restOperation.response;
      const response = await body.json();
      if (statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("Suggestion successfully added"),
        });
        dispatch({ type: "DATA_RECEIVED", value: response });
        tlmGetSuggestion();
      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to add suggestion"),
        });
      }
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to add suggestion"),
      });

      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };

  const handleEditClick = () => {
    setIsEditable(!isEditable);
  };

  const handleInputChange = (language, value) => {
    setSuggestions((prevSuggestions) => ({
      ...prevSuggestions,
      [language]: value,
    }));
  };

  const handleSave = () => {
    tlmNewSuggestion();
    setIsEditable(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        {I18n.get("Suggestion")}
        <div style={{ position: "absolute", right: 16, top: 8 }}>
          {isEditable && (
            <>
              <Tooltip title={I18n.get("Cancel")}>
                <IconButton
                  aria-label="Cancel"
                  onClick={handleEditClick}
                  sx={{ marginRight: 1 }}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={I18n.get("Save")}>
                <IconButton aria-label="Save" onClick={handleSave}>
                  <SaveIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          {!isEditable && (
            <Tooltip title={I18n.get("Edit")}>
              <IconButton aria-label="Edit" onClick={handleEditClick}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {Object.entries(suggestions).map(([language, value]) => (
            <TextField
              key={language}
              label={language}
              value={value || ""}
              onChange={(e) => handleInputChange(language, e.target.value)}
              InputProps={{
                readOnly: !isEditable,
              }}
              variant="outlined"
              fullWidth
              multiline
            />
          ))}
        </Stack>
      </DialogContent>
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
    </Dialog>
  );
};

export default SuggestionDialog;
